import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
} from '@mui/material';
import { Client, UpdateClient } from '../types/client';
import { crmService } from '../services/crmService';

interface ModifyClientModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  client: Client | null;
}

const ModifyClientModal: React.FC<ModifyClientModalProps> = ({ open, onClose, onSave, client }) => {
  const [clientData, setClientData] = useState<UpdateClient>({});
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setClientData({
        name: client.name,
        email: client.email,
        phone: client.phone,
        address: client.address,
      });
    }
  }, [client]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientData((prev: UpdateClient) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!client) return;

    setIsSaving(true);
    setError(null);
    try {
      await crmService.updateClient(client.id, clientData);
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Failed to update client:', err);
      const errorData = err.response?.data;
      const errorMessage = errorData ? Object.values(errorData).join(' ') : 'An unexpected error occurred.';
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Modify Client</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField margin="normal" required fullWidth label="Client Name" name="name" value={clientData.name || ''} onChange={handleChange} autoFocus />
          <TextField margin="normal" fullWidth label="Email Address" name="email" type="email" value={clientData.email || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="Phone Number" name="phone" value={clientData.phone || ''} onChange={handleChange} />
          <TextField margin="normal" fullWidth label="Address" name="address" multiline rows={3} value={clientData.address || ''} onChange={handleChange} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModifyClientModal;